import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { CircularProgress, Stack } from '@mui/material'
import React, { useEffect } from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateInstallation } from 'src/Libs/installations/api/installation.api'
import { useLocationConfigurationFormInitializer } from 'src/Libs/installations/hooks/installation-configuration/use-location-configuration-form-initializer'
import { Installation, InstallationFormModel, LocationConfigurationFormModel } from '../../../../models/installation.model'
import SaveConfigurationButton from './SaveConfigurationButton'
import { useUser } from 'src/Hooks/useUser'
import { SettingsBox } from 'src/Elements/Data'
import LocationForm from 'src/Libs/installations/components/new-installation/configuration/general/LocationForm'
import { usePortalMutation } from 'src/Libs/api'

type LocationConfigurationProps = {
  installation?: Installation
  installationId?: number
  installationLoading: boolean
}

const LocationConfiguration: React.FC<LocationConfigurationProps> = ({ installation, installationId, installationLoading }) => {
  const { t } = useCustomTranslation()
  const { contactData } = useUser()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const { initialValues, validationSchema } = useLocationConfigurationFormInitializer(installation)

  const methods = useForm<LocationConfigurationFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    methods.reset(initialValues)
  }, [initialValues, methods])

  const { mutate: updateLocation, isPending: isUpdatingLocation } = usePortalMutation({
    mutationFn: async (payload: Partial<InstallationFormModel>) => {
      if (!installationId) {
        throw new Error('no installationId provided')
      }

      const result = await updateInstallation(installationId, payload, authFetch)

      if (result.hasErrors) {
        throw result.error ?? new Error('Failed to update installation location')
      }

      return result
    },
    onSuccess: () => {
      enqueueSuccessStack(t('installation-details.change-location.save.success'))
    },
    onError: error => {
      const err = error as AuthFetchError

      if (!contactData?.Email) {
        enqueueErrorStack(t('installation-details.change-location.save.error-no-email'))
      } else if (err.response?.data.message === FetchErrorCodes.OwnerNotSet.toString()) {
        enqueueErrorStack(t('installation-details.change-location.save.error-no-owner'))
      } else if (err.response?.data.message === FetchErrorCodes.OwnerHasNoAddress.toString()) {
        enqueueErrorStack(t('installation-details.change-location.save.error-no-owner-address'))
      } else {
        enqueueErrorStack(t('installation-details.change-location.save.error'))
      }

      console.error('error saving installation location', error)
    },
  })

  const isFormDisabled = isUpdatingLocation

  const onSubmit = (data: LocationConfigurationFormModel): void => {
    updateLocation({
      streetName: data.streetName,
      streetNumber: data.streetNumber,
      zip: data.zip,
      city: data.city,
      countryCode: data.countryCode,
      storageLocationEmail: installation?.ownerId ? undefined : contactData?.Email,
    })
  }

  return (
    <AccordionItem title={t('installation-configuration.location-settings.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        {installationLoading ? (
          <CircularProgress />
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <SettingsBox
                gridSizes={{ smLeft: 12, smRight: 12 }}
                title={'installation-configuration.location-settings.form.title'}
                suffix={<LocationForm isDisabled={isFormDisabled} />}
              />
              <SaveConfigurationButton isLoading={isUpdatingLocation} />
            </form>
          </FormProvider>
        )}
      </Stack>
    </AccordionItem>
  )
}

export default LocationConfiguration
